import React from "react";
import { Helmet } from "react-helmet-async";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Canonical / OG URLs should ALWAYS point to production so that preview
// deployments never compete with production for Google rankings.
const SITE_ORIGIN =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" && window.location.origin) ||
  BACKEND_URL;

// Preview environments should never be indexed by search engines.
const IS_PREVIEW =
  typeof window !== "undefined" && /preview\.emergentagent\.com/i.test(window.location.hostname);

const DEFAULTS = {
  siteName: "360 View LLC",
  titleSuffix: "360 View LLC · Solar Surveillance Trailers Nevada",
  description:
    "Solar-powered mobile and modular surveillance trailers for construction, commercial, government and remote sites in Nevada and Utah. Rapid deployment. Cellular connected. Remote video access.",
  ogImage: "/logo.jpg",
};

/**
 * Per-page SEO wrapper: title / description / canonical / Open Graph / Twitter / JSON-LD.
 * Usage: <Seo title="Mini Solar Pole" description="…" path="/systems/mini-solar-pole-system" schema={{...}} />
 */
export default function Seo({
  title,
  description,
  path,
  ogImage,
  noindex = false,
  schema,
  breadcrumb,
}) {
  const fullTitle = title ? `${title} · ${DEFAULTS.siteName}` : DEFAULTS.titleSuffix;
  const desc = description || DEFAULTS.description;
  const canonical = path ? `${SITE_ORIGIN}${path}` : null;
  const image = ogImage || `${SITE_ORIGIN}${DEFAULTS.ogImage}`;

  const schemaList = [];
  if (Array.isArray(schema)) schemaList.push(...schema);
  else if (schema) schemaList.push(schema);
  if (breadcrumb && breadcrumb.length) {
    schemaList.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumb.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: b.path ? `${SITE_ORIGIN}${b.path}` : undefined,
      })),
    });
  }

  return (
    <Helmet>
      {title && <title>{fullTitle}</title>}
      {description && <meta name="description" content={desc} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {(noindex || IS_PREVIEW) && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph — only when path is set (page-level) */}
      {path && <meta property="og:type" content="website" />}
      {path && <meta property="og:site_name" content={DEFAULTS.siteName} />}
      {path && title && <meta property="og:title" content={fullTitle} />}
      {path && <meta property="og:description" content={desc} />}
      {canonical && <meta property="og:url" content={canonical} />}
      {path && <meta property="og:image" content={image} />}

      {/* Twitter */}
      {path && <meta name="twitter:card" content="summary_large_image" />}
      {path && title && <meta name="twitter:title" content={fullTitle} />}
      {path && <meta name="twitter:description" content={desc} />}
      {path && <meta name="twitter:image" content={image} />}

      {schemaList.map((s) => {
        const key = `${s["@type"] || "schema"}-${(s.name || s.headline || s.serviceType || "").slice(0, 40) || Object.keys(s).length}`;
        return (
          <script key={key} type="application/ld+json">{JSON.stringify(s)}</script>
        );
      })}
    </Helmet>
  );
}

/** Reusable schema builders for consistent JSON-LD across pages. */
export const orgSchema = (settings = {}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_ORIGIN}#business`,
  name: settings.business_name || "360 View LLC",
  url: SITE_ORIGIN,
  telephone: settings.phone || "702-929-0390",
  email: settings.email || "360viewlv@gmail.com",
  image: `${SITE_ORIGIN}/logo.jpg`,
  logo: `${SITE_ORIGIN}/logo.jpg`,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Las Vegas",
    addressRegion: "NV",
    addressCountry: "US",
  },
  areaServed: (settings.states_served || ["Nevada", "Utah"]).map((s) => ({ "@type": "State", name: s })),
  sameAs: Object.values(settings.social_links || {}).filter(Boolean),
  description:
    "Solar-powered mobile and modular surveillance trailers and pole systems for construction, commercial, government and remote sites in Nevada and Utah.",
});

export const productSchema = (system) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: system.title,
  description: system.summary,
  image: system.image,
  brand: { "@type": "Brand", name: "360 View LLC" },
  category: "Mobile surveillance trailer",
});

export const serviceSchema = (svc) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: svc.title,
  name: svc.title,
  description: svc.summary,
  provider: { "@type": "LocalBusiness", name: "360 View LLC", telephone: "702-929-0390" },
  areaServed: [{ "@type": "State", name: "Nevada" }, { "@type": "State", name: "Utah" }],
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const articleSchema = (a) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.title,
  description: a.excerpt,
  author: { "@type": "Person", name: a.author || "360 View Team" },
  datePublished: a.published_date,
  dateModified: (a.updated_at || a.published_date || "").substring(0, 10),
  publisher: { "@type": "Organization", name: "360 View LLC", logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/logo.jpg` } },
});
